import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiPlus, FiChevronRight, FiUsers, FiSearch, FiGrid, FiList, FiArrowRight, FiTrash2, FiArchive, FiRotateCcw } from 'react-icons/fi';
import ClientOnboardingModal from '../components/ClientOnboardingModal';

function Clients() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const FOUNDER_EMAIL = 'nishantvarma143@gmail.com';
  const isFounder = user?.email === FOUNDER_EMAIL;
  
  const [clients, setClients] = useState([]);
  const [archivedClients, setArchivedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);
  const [deletingClientId, setDeletingClientId] = useState(null);
  const [restoringClientId, setRestoringClientId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [clientLimit, setClientLimit] = useState(1);

  useEffect(() => {
    loadClients();
    loadArchivedClients();
    fetchClientLimit();
    
    // Check if we should auto-open onboarding (from failed demo or welcome flow)
    if (location.state?.showOnboarding) {
      setShowModal(true);
      setShowWelcomeGuide(true);
      // Clear the state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchClientLimit = async () => {
    try {
      const usage = await api.getUserUsage();
      // Backend returns active_clients, check both for compatibility
      const limit = usage.limits?.active_clients || usage.limits?.clients || 1;
      // -1 or 999 means unlimited
      setClientLimit(limit >= 999 ? -1 : limit);
    } catch (err) {
      console.error('Error fetching client limit:', err);
    }
  };

  const loadClients = async () => {
    try {
      const data = await api.listClients();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadArchivedClients = async () => {
    try {
      const data = await api.listArchivedClients();
      setArchivedClients(data);
    } catch (error) {
      console.error('Error loading archived clients:', error);
    }
  };

  const handleClientCreated = async (newClient) => {
    setShowModal(false);
    setShowWelcomeGuide(false);
    
    // If this was a first-time user creating their first client, 
    // navigate them directly to the client detail page to continue the flow
    if (clients.length === 0 && newClient?.id) {
      navigate(`/clients/${newClient.id}`, { 
        state: { newClient: true, showCampaignHint: true } 
      });
    } else {
      loadClients();
    }
  };

  const handleArchiveClient = async (e, clientId, clientName, isDemo) => {
    e.stopPropagation(); // Prevent navigation to client detail
    
    const confirmMessage = isDemo 
      ? `Archive demo client "${clientName}"? You can restore it within 30 days.`
      : `Archive "${clientName}"? All campaigns and content will be preserved. You can restore within 30 days.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    setDeletingClientId(clientId);
    try {
      await api.deleteClient(clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
      loadArchivedClients(); // Refresh archived list
    } catch (error) {
      console.error('Failed to archive client:', error);
      alert('Failed to archive client. Please try again.');
    } finally {
      setDeletingClientId(null);
    }
  };

  const handleRestoreClient = async (e, clientId) => {
    e.stopPropagation();
    
    setRestoringClientId(clientId);
    try {
      await api.restoreClient(clientId);
      setArchivedClients(prev => prev.filter(c => c.id !== clientId));
      loadClients(); // Refresh active clients
    } catch (error) {
      console.error('Failed to restore client:', error);
      const message = error.response?.data?.detail || 'Failed to restore client. Please try again.';
      alert(message);
    } finally {
      setRestoringClientId(null);
    }
  };

  const handlePermanentDelete = async (e, clientId, clientName) => {
    e.stopPropagation();
    
    const confirmMessage = `PERMANENTLY delete "${clientName}"? This cannot be undone. All campaigns, content, and data will be lost forever.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Double confirmation for permanent delete
    if (!window.confirm('Are you absolutely sure? This action is irreversible.')) return;
    
    setDeletingClientId(clientId);
    try {
      await api.permanentlyDeleteClient(clientId);
      setArchivedClients(prev => prev.filter(c => c.id !== clientId));
    } catch (error) {
      console.error('Failed to permanently delete client:', error);
      alert('Failed to delete client. Please try again.');
    } finally {
      setDeletingClientId(null);
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDaysUntilExpiry = (archivedAt) => {
    if (!archivedAt) return 30;
    const archivedDate = new Date(archivedAt);
    const now = new Date();
    const daysSinceArchived = Math.floor((now - archivedDate) / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - daysSinceArchived);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading clients...</p>
        </div>
      </div>
    );
  }

  // Only founders or users with no brands can add a brand
  const canAddClient = clients.length === 0 || isFounder;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Your Brands</h1>
          <p className="text-muted-foreground mt-1">
            {clients.length === 0 
              ? "Set up your brand to start creating content" 
              : "Your brand profile"}
          </p>
        </div>
        {/* Only show Add Brand button if user has no brands OR is founder */}
        {canAddClient && (
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/20"
            data-testid="add-brand-btn"
          >
            <FiPlus size={18} />
            {clients.length === 0 ? "Set Up Your Brand" : "Add Brand"}
          </button>
        )}
      </div>

      {clients.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative w-full sm:w-72">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="text"
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-card border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
            />
          </div>
          
          {/* View toggle */}
          <div className="flex items-center gap-2 p-1 bg-secondary rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <FiGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <FiList size={18} />
            </button>
          </div>
        </div>
      )}

      {clients.length === 0 ? (
        <div className="bg-card rounded-xl p-12 text-center border border-border/50">
          <div className="max-w-lg mx-auto">
            <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FiUsers className="text-primary" size={36} />
            </div>
            <h3 className="text-2xl font-heading font-bold text-foreground mb-3">
              Let&apos;s Set Up Your Brand
            </h3>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Start by creating your brand profile. FirstCut will remember your brand voice, visual style, 
              and audience — so every piece of content feels authentically you.
            </p>
            
            {/* Step preview */}
            <div className="grid grid-cols-3 gap-3 mb-8 text-left">
              {[
                { num: 1, title: "Brand Identity", desc: "Name, tone, values" },
                { num: 2, title: "Target Audience", desc: "Who you're speaking to" },
                { num: 3, title: "Goals & Style", desc: "What you want to achieve" }
              ].map((step) => (
                <div key={step.num} className="bg-secondary/50 rounded-lg p-3">
                  <div className="w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold mb-2">
                    {step.num}
                  </div>
                  <div className="text-xs font-medium text-foreground">{step.title}</div>
                  <div className="text-xs text-muted-foreground">{step.desc}</div>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => setShowModal(true)}
              className="px-8 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all text-lg shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98]"
              data-testid="setup-first-client-btn"
            >
              <FiPlus className="inline-block mr-2 -mt-0.5" size={20} />
              Set Up Your First Client
            </button>
            <p className="text-xs text-muted-foreground mt-4">Takes about 2 minutes</p>
          </div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="bg-card rounded-xl p-12 text-center border border-border/50">
          <p className="text-muted-foreground">No clients match your search</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              onClick={() => navigate(`/clients/${client.id}`)}
              className={`group bg-card rounded-xl p-6 border transition-all cursor-pointer hover-lift ${client.is_demo ? 'border-violet-500/30 bg-violet-500/5' : 'border-border/50 hover:border-primary/30'}`}
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${client.is_demo ? 'bg-violet-500/20' : 'bg-gradient-to-br from-primary/20 to-accent/20'}`}>
                  <span className={`font-bold text-xl ${client.is_demo ? 'text-violet-400' : 'text-primary'}`}>
                    {client.name?.charAt(0).toUpperCase() || 'C'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {client.is_demo && (
                    <span className="px-2.5 py-1 bg-violet-500/20 text-violet-400 text-xs rounded-full border border-violet-500/30 font-medium">
                      Demo Client
                    </span>
                  )}
                  <button
                    onClick={(e) => handleArchiveClient(e, client.id, client.name, client.is_demo)}
                    disabled={deletingClientId === client.id}
                    className="p-1.5 text-muted-foreground hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    title="Archive client"
                  >
                    {deletingClientId === client.id ? (
                      <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FiArchive size={16} />
                    )}
                  </button>
                  <FiChevronRight className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-foreground mb-1 truncate">{client.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{client.industry || 'No industry set'}</p>
              
              {client.is_demo && (
                <p className="text-xs text-violet-400/70 mb-3 italic">Sample client for exploring the platform</p>
              )}
              
              <div className="flex flex-wrap gap-2">
                {client.brand_tone && (
                  <span className="px-2.5 py-1 bg-secondary text-foreground rounded-md text-xs font-medium">
                    {client.brand_tone}
                  </span>
                )}
                {client.goals && client.goals.slice(0, 2).map((goal, idx) => (
                  <span key={idx} className="px-2.5 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium">
                    {goal}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
          <div className="divide-y divide-border/30">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                onClick={() => navigate(`/clients/${client.id}`)}
                className={`group flex items-center gap-4 p-4 hover:bg-secondary/50 transition-colors cursor-pointer ${client.is_demo ? 'bg-violet-500/5' : ''}`}
                data-testid={`client-row-${client.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${client.is_demo ? 'bg-violet-500/20' : 'bg-gradient-to-br from-primary/20 to-accent/20'}`}>
                  <span className={`font-semibold ${client.is_demo ? 'text-violet-400' : 'text-primary'}`}>
                    {client.name?.charAt(0).toUpperCase() || 'C'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground truncate">{client.name}</p>
                    {client.is_demo && (
                      <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full border border-violet-500/30 font-medium">
                        Demo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{client.industry || 'No industry set'}</p>
                </div>
                <div className="hidden md:flex gap-2">
                  {client.brand_tone && (
                    <span className="px-2.5 py-1 bg-secondary text-foreground rounded-md text-xs font-medium">
                      {client.brand_tone}
                    </span>
                  )}
                </div>
                <button
                  onClick={(e) => handleArchiveClient(e, client.id, client.name, client.is_demo)}
                  disabled={deletingClientId === client.id}
                  className="p-1.5 text-muted-foreground hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  title="Archive client"
                >
                  {deletingClientId === client.id ? (
                    <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FiArchive size={16} />
                  )}
                </button>
                <FiChevronRight className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archived Clients Section */}
      {archivedClients.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <FiArchive size={18} />
            <span className="font-medium">Archived Clients ({archivedClients.length})</span>
            <FiChevronRight className={`transition-transform ${showArchived ? 'rotate-90' : ''}`} size={16} />
          </button>
          
          {showArchived && (
            <div className="bg-card/50 rounded-xl border border-border/30 overflow-hidden">
              <div className="divide-y divide-border/20">
                {archivedClients.map((client) => {
                  const daysLeft = getDaysUntilExpiry(client.archived_at);
                  return (
                    <div
                      key={client.id}
                      className="group flex items-center gap-4 p-4 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <div className="w-10 h-10 bg-secondary/50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-muted-foreground font-semibold">
                          {client.name?.charAt(0).toUpperCase() || 'C'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground/70 truncate">{client.name}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {daysLeft > 0 ? `${daysLeft} days left to restore` : 'Expiring soon'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleRestoreClient(e, client.id)}
                          disabled={restoringClientId === client.id}
                          className="px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        >
                          {restoringClientId === client.id ? (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <FiRotateCcw size={14} />
                          )}
                          Restore
                        </button>
                        <button
                          onClick={(e) => handlePermanentDelete(e, client.id, client.name)}
                          disabled={deletingClientId === client.id}
                          className="px-3 py-1.5 text-red-400 hover:bg-red-500/10 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        >
                          {deletingClientId === client.id ? (
                            <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <FiTrash2 size={14} />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <ClientOnboardingModal
          onClose={() => setShowModal(false)}
          onSuccess={handleClientCreated}
        />
      )}
    </div>
  );
}

export default Clients;
